/**
 * 浏览器端加密工具（来自 qrPay 项目）
 * 使用Crypto-JS的EVP_BytesToKey格式（Salted__格式）
 * 对应后端的crypto-js兼容解密路径
 */

import CryptoJS from 'crypto-js'
import WxmpRsa from 'wxmp-rsa'

// RSA公钥（与后端验证代码一致）
const RSA_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAyrToggnzL0RO5jm2hQaF
GbnHhYR17rU7XXoQ7hQoNVZDrVYqhX0PAh95tGhQjO/tm3lbhaTAZnb5CjLVRMun
m/M9yPcbNLgRdN7cZ48FLNE2TUqwBj2EJSVzml7xXDdQFEr1JNK3RS1as1qg3exY
rxMHuneS/C7UHIULDUkumQuinc9y0u1oD34p0leWJMjAim8oUNUzCjVDUj8T1t3b
9EIksrjfXWYxUP2D4PXloeSbjlea0pebxelek3XnagY1eSHmEdGtY3q8fFkS1KIz
av+aF60DyApcjSVa5Q0iSpbK1bUbBssbuLzTE/fIHMKc2OEqz0Un4YiX2yWSwzCi
TwIDAQAB
-----END PUBLIC KEY-----`

/**
 * RSA加密（使用 wxmp-rsa，支持2048位RSA）
 * @param {string} binaryString - 需要加密的二进制字符串（Latin1编码）
 * @returns {string} 返回BASE64编码的加密字符串
 */
function rsaEncrypt(binaryString) {
  try {
    const rsa = new WxmpRsa()
    rsa.setPublicKey(RSA_PUBLIC_KEY)

    // wxmp-rsa 支持长字符串加密（2048位RSA）
    return rsa.encryptLong(binaryString)
  } catch (error) {
    console.error('RSA加密错误:', error)
    throw error
  }
}

/**
 * 加密请求数据
 * @param {Object} data - 原始数据对象
 * @returns {Object} - 加密后的数据 {encryptedData, encryptedKey}
 */
export function encryptRequest(data) {
  try {
    console.log('========================================')
    console.log('[Crypto-JS EVP格式加密] 开始')
    console.log('========================================')

    // 1. 生成随机AES密钥(32字节 = 256位)
    // 这个密钥会被RSA加密，后端解密后用于EVP_BytesToKey派生
    const aesKey = CryptoJS.lib.WordArray.random(32)
    console.log('[1] AES密钥已生成: 32字节')

    // 2. 将数据转为JSON字符串
    const jsonData = JSON.stringify(data)
    console.log('[2] 明文:', jsonData)

    // 3. 将密钥转为字符串（Latin1编码，用于CryptoJS加密）
    // 重要：传入字符串作为key，CryptoJS会自动使用EVP_BytesToKey派生
    const keyString = CryptoJS.enc.Latin1.stringify(aesKey)

    // 4. 使用CryptoJS加密，自动生成Salted__格式
    // 当key是字符串时，CryptoJS会：
    // - 生成随机salt
    // - 使用EVP_BytesToKey(keyString, salt)派生真正的加密密钥和IV
    // - 返回 "Salted__" + salt + 密文 的格式
    const encrypted = CryptoJS.AES.encrypt(jsonData, keyString)

    console.log('[3] AES-256-CBC加密完成 (EVP_BytesToKey格式)')

    // 5. encrypted.toString() 会自动返回Base64格式
    // 包含 "Salted__" + salt + 密文
    const encryptedData = encrypted.toString()

    console.log('[4] 加密数据格式: Salted__ + Salt(8字节) + 密文')
    console.log('[4] Base64长度:', encryptedData.length, '字符')

    // 验证是否包含Salted__前缀
    const decoded = CryptoJS.enc.Base64.parse(encryptedData)
    const prefix = CryptoJS.enc.Latin1.stringify(decoded).substring(0, 8)
    console.log('[5] 验证前缀:', prefix === 'Salted__' ? '✓ 正确' : '✗ 错误')

    // 6. 使用RSA公钥加密原始AES密钥（32字节）
    console.log('[6] RSA加密AES密钥...')

    // 将32字节AES密钥转为二进制字符串（Latin1编码）
    const aesKeyBinary = CryptoJS.enc.Latin1.stringify(aesKey)
    const encryptedKey = rsaEncrypt(aesKeyBinary)

    if (!encryptedKey) {
      throw new Error('RSA加密失败')
    }

    console.log('[7] RSA加密完成, Base64长度:', encryptedKey.length, '字符')
    console.log('========================================')
    console.log('[加密] 完成!')
    console.log('后端将使用EVP_BytesToKey解密路径')
    console.log('========================================')

    return {
      encryptedData,
      encryptedKey
    }

  } catch (error) {
    console.error('========================================')
    console.error('[错误]', error.message)
    console.error('========================================')
    throw error
  }
}

/**
 * 导出RSA公钥（用于调试）
 */
export const getPublicKey = () => RSA_PUBLIC_KEY
